import { Link } from "react-router-dom";
import CalendarSyncCard from "../components/CalendarSyncCard";
import { CaretLeft, Calendar } from "@phosphor-icons/react";

/**
 * Standalone calendar-sync page — accessible without login.
 *
 * Customers reach this page in 4 ways:
 *   1. The "Set up" action in the first-follow toast
 *   2. The "Calendar" link in the navbar
 *   3. The "Sync to calendar" button on every truck profile (/t/:slug)
 *   4. Direct deep-link from Settings → "open in a new tab" for sharing
 *
 * Uses `follower_id` from localStorage so anonymous followers can
 * subscribe their phone calendar without creating an account.
 * Logged-in users see the exact same UI — both flows write to the
 * `followers` collection keyed on `follower_id`.
 */
export default function CalendarSync() {
  return (
    <div className="mx-auto max-w-2xl px-4 py-8 sm:py-12">
      <Link
        to="/"
        className="inline-flex items-center text-xs text-muted-foreground hover:text-foreground mb-4"
        data-testid="calendar-sync-back"
      >
        <CaretLeft weight="bold" className="h-3 w-3 mr-1" /> Back to map
      </Link>

      <div className="mb-6">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold uppercase tracking-wider mb-3">
          <Calendar weight="duotone" className="h-3.5 w-3.5" />
          Calendar sync
        </div>
        <h1 className="font-display font-bold text-3xl sm:text-4xl tracking-tight">
          Never miss your favorite truck again.
        </h1>
        <p className="mt-3 text-sm sm:text-base text-muted-foreground leading-relaxed">
          Subscribe your phone&apos;s calendar to TruckTrackr and every truck you
          follow appears on your calendar automatically — for the next 60 days.
          New events get added. Cancellations get removed. Zero maintenance.
        </p>
      </div>

      <CalendarSyncCard />

      <div className="mt-8 grid sm:grid-cols-3 gap-3 text-xs">
        <Bullet title="Subscribe once">
          Tap a button on this page to set up your subscription. Done in 10 seconds.
        </Bullet>
        <Bullet title="Always up to date">
          Auto-refreshes every ~8-24h depending on your calendar app.
        </Bullet>
        <Bullet title="No notifications">
          Calendar alarms are stripped so we don&apos;t spam you. Push notifications
          still work the same.
        </Bullet>
      </div>
    </div>
  );
}

function Bullet({ title, children }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="font-semibold text-sm mb-1">{title}</div>
      <div className="text-muted-foreground leading-relaxed">{children}</div>
    </div>
  );
}
